import { env } from '@/env';

export interface AddressValidationInput {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface AddressValidationCorrection {
  streetAddress: string;
  city: string;
  zipCode: string;
}

export interface AddressValidationVerdict {
  isValid: boolean;
  // Present only when Google could not fully confirm the address AS TYPED but
  // proposes a corrected version. State is intentionally omitted — Google
  // returns it abbreviated (e.g. "CA"), which would not match the full state
  // names our state select/zip-range validation expects.
  correction?: AddressValidationCorrection;
}

interface GoogleAddressValidationResponse {
  result?: {
    verdict?: {
      addressComplete?: boolean;
      hasUnconfirmedComponents?: boolean;
    };
    address?: {
      postalAddress?: {
        locality?: string;
        postalCode?: string;
        addressLines?: string[];
      };
    };
  };
}

const ADDRESS_VALIDATION_URL = 'https://addressvalidation.googleapis.com/v1:validateAddress';

/**
 * Google Address Validation API — the accuracy gate behind Places
 * Autocomplete. Runs on shipping-form submit so even a hand-typed (not
 * autocompleted) address that happens to be regex-valid but not a real,
 * deliverable address is caught. Degrades to "valid" (never blocks submit) on
 * a missing key or a Google API/network failure — this is an accuracy
 * enhancement layered on top of the existing regex validation, not a single
 * point of failure for the whole step.
 */
export async function validateAddress(input: AddressValidationInput): Promise<AddressValidationVerdict> {
  const apiKey = env.GOOGLE_PLACES_API_KEY;
  if (!apiKey) return { isValid: true };

  try {
    const response = await fetch(`${ADDRESS_VALIDATION_URL}?key=${encodeURIComponent(apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        address: {
          regionCode: 'US',
          addressLines: [input.streetAddress],
          locality: input.city,
          administrativeArea: input.state,
          postalCode: input.zipCode,
        },
      }),
    });

    if (!response.ok) return { isValid: true };

    const data = (await response.json()) as GoogleAddressValidationResponse;
    const verdict = data.result?.verdict;
    const isValid = verdict?.addressComplete === true && verdict?.hasUnconfirmedComponents !== true;

    if (isValid) return { isValid: true };

    const postal = data.result?.address?.postalAddress;
    if (!postal) return { isValid: false };

    return {
      isValid: false,
      correction: {
        streetAddress: postal.addressLines?.[0] ?? input.streetAddress,
        city: postal.locality ?? input.city,
        zipCode: postal.postalCode ?? input.zipCode,
      },
    };
  } catch {
    return { isValid: true };
  }
}
